import type { Theme } from '@scalar-org/entities/theme'
import {
  type ThemeId as ExternalThemeId,
  presets as presetStyles,
  themeLabels,
} from '@scalar/themes'
import { nanoid } from 'nanoid'
import { readonly } from 'vue'

import customStyles from './custom.css?inline'
export * from './document-classes'
import { slugify } from '@scalar-org/helpers/url'

/**
 * Stylesheets to extend the open source themes for the docs app
 */
const extensionStyles: Record<string, string> = import.meta.glob(
  './extensions/*.css',
  {
    query: '?raw',
    import: 'default',
    eager: true,
  },
)
type ThemeId = Exclude<
  ExternalThemeId,
  'none' | 'elysiajs' | 'fastify' | 'laserwave'
>

/** Theme objects require fixed UIDs to match data type of user themes */
const PresetUids: { [key in ThemeId]: string } = {
  alternate: '2skUDSH4S8HYFF9yXysr-',
  default: 'qTQR9jSM8E-LihpyZzPOi',
  moon: 'DG9ZUNp5lJhDeX_kPX4Bl',
  purple: 'pE_1ysxcZ-y2LM1GGNBUv',
  solarized: 'BdGVG1vf-4nYl3wJKyj8l',
  bluePlanet: 'X12IfAvl7ue-42V2lW40S',
  deepSpace: 'K8b38NWQiicq4-zXGXKdI',
  saturn: '1jyAjmbIZQG-RUU4Ugk9o',
  kepler: 'jZ6dnWbtqQ0Hz3s9jLPH0',
  mars: 'YY4LQgwiXix55-TmMz9qd',
}

/**
 * Merges the preset styles with any extension styles for a given theme
 */
const getStyles = (id: ThemeId) => {
  const styles = [presetStyles[id]]

  // If there's an extension stylesheet for this theme add it
  const extensionFile = `./extensions/${id}.css`
  if (extensionStyles[extensionFile])
    styles.push(extensionStyles[extensionFile])

  return styles.join('\n')
}

/** Return a standard theme object based on a preset name*/
const getTheme = (id: ThemeId): Theme => ({
  name: themeLabels[id],
  slug: slugify(themeLabels[id]),
  description: '',
  uid: PresetUids[id],
  theme: getStyles(id),
})

/** Default theme for all projects */
export const fallbackTheme = getTheme('default')

/** Starter custom theme styles with all variables and options */
export const getStarterTheme = (name: string): Theme => ({
  name,
  slug: slugify(name),
  description: '',
  uid: nanoid(),
  theme: customStyles,
})

/** List of theme presets */
export const themePresets: Readonly<Theme[]> = readonly([
  getTheme('default'),
  getTheme('alternate'),
  getTheme('purple'),
  getTheme('solarized'),
  getTheme('moon'),
  getTheme('bluePlanet'),
  getTheme('deepSpace'),
  getTheme('saturn'),
  getTheme('kepler'),
  getTheme('mars'),
])
